package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.EfficacyCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Tilt (Creates a 2D card flip/settle effect)
@Composable
private fun Modifier.staggeredTiltIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).rotate(if (isVisible) 0.deg else (-5).deg)
        .scale(if (isVisible) 1f else 0.9f).transition(
            Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun ProductClinicalEfficacySection(data: ProductClinicalEfficacySectionData = defaultProductEfficacySectionData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-clinical-efficacy-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.efficacies.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).staggeredTiltIn(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2, lg = 4),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(40.px)
                    ) {
                        data.efficacies.take(4).forEachIndexed { index, efficacy ->
                            key(efficacy.title) {
                                EfficacyCard(
                                    efficacy.iconSrc,
                                    efficacy.title,
                                    efficacy.description,
                                    Modifier.staggeredTiltIn(isVisible, 200 + (index * 150))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EfficacyCard(iconSrc: String, title: String, description: String, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        modifier = EfficacyCardStyle.toModifier().then(modifier).fillMaxWidth().height(100.percent)
            .backgroundColor(Colors.White).borderRadius(12.px)
            .boxShadow(0.px, 4.px, 20.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.06f)).padding(32.px),
        horizontalAlignment = Alignment.Start
    ) {
        if (iconSrc.isNotBlank()) Image(
            src = iconSrc, modifier = Modifier.unextractable().unextractable().size(48.px).margin(bottom = 24.px)
        )
        if (title.isNotBlank()) SpanText(
            text = title,
            modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).fontSize(18.px)
                .margin(bottom = 12.px)
        )
        if (description.isNotBlank()) Div(
            attrs = Modifier.color(palette.custom.darkText).fontSize(16.px)
            .lineHeight(1.5).styleModifier {
                property("display", "-webkit-box"); property(
                "-webkit-line-clamp", "5"
            ); property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
            }.toAttrs { ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} } })
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field iconSrc:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px.
 * - Format: SVG or transparent PNG.
 */
data class EfficacyData(val iconSrc: String, val title: String, val description: String)
data class ProductClinicalEfficacySectionData(
    val sectionId: String? = null, val sectionHeader: String, val efficacies: List<EfficacyData>
)

val defaultProductEfficacySectionData = ProductClinicalEfficacySectionData(
    sectionId = "product-clinical-efficacy-section",
    sectionHeader = "FOUNDATION OF THE MyHand<sup>®</sup> SYSTEM",
    efficacies = listOf(
        EfficacyData(
            Res.Images.ProductPage.Efficacy.ICON_THERAPY,
            "TARGETED FUNCTIONAL THERAPY",
            "Easily adaptable to multiple patient conditions; highly engaging interface with animated finger motion display."
        ), EfficacyData(
            Res.Images.ProductPage.Efficacy.ICON_EFFICACY,
            "CLINICAL EFFICACY",
            "Demonstrated clinical efficacy in multiple studies using 9 industry standard rehabilitation tests."
        ), EfficacyData(
            Res.Images.ProductPage.Efficacy.ICON_NEURO,
            "BUILT ON NEUROSCIENCE FOUNDATION",
            "Uses the fundamentals of neuroscience and principles of neuroplasticity for motor control recovery."
        ), EfficacyData(
            Res.Images.ProductPage.Efficacy.ICON_CLOUD,
            "CLOUD CONNECTED",
            "Artificial Intelligence (AI) driven clinical data analytics on hand function metrics for future personalized rehabilitation."
        )
    ) // Your existing payload
)